import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, tableDividerColumnWithAgg, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { AgtFitnessVm, FitModel } from 'app/stores/ui/agt.fitness.ui.store';
import { formatPercent, formatPercentWithIcon } from 'app/utils';
import { Icon } from 'app/components/common/Icon';
import { useLocation } from 'react-router';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import routes from 'routes';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';

export const simpleFitCell = (f: FitModel, showCalc: boolean = true) => {
	if (!f || !f.ergebnis) return <></>;

	return (
		<>
			<span>{formatPercent(f.ze, 1)}% </span>
			{showCalc && (
				<span>
					<i>
						({f.ergebnis} / {f.ziel})
					</i>
				</span>
			)}
		</>
	);
};

export const FitnessTrackerListe = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);

	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		// GOOGLE
		cols.push({
			label: 'GUP Durchschnitt',
			path: `bewAvg`,
			sortBy: `bewAvg.ergebnis`,
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			render(row) {
				return simpleFitCell(row.data.gupBewAvg, false);
			},
			agg: {
				path: 'bewAvg.ergebnis',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		/// AMIS.NOW
		cols.push({
			label: 'AMIS.NOW Aufgaben',
			path: `amisNowQuoteRead.ergebnis`,
			sortBy: `amisNowQuoteRead.ergebnis`,
			cellModifier: 'border-left-turquoise bg-turquoise',
			labelModifier: 'border-left-turquoise bg-turquoise',
			render(row) {
				return simpleFitCell(row.data.amisNowQuoteRead);
			},
			agg: {
				path: 'amisNowQuoteRead.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		cols.push({
			label: 'AKUMA Planer Übertragungsquote',
			path: `planerUebertragungquoteAmisNow.ze`,
			sortBy: `planerUebertragungquoteAmisNow.ze`,
			cellModifier: 'border-left-yellow bg-yellow',
			labelModifier: 'border-left-yellow  bg-yellow',
			render(row) {
				return simpleFitCell(row.data.planerUebertragungquoteAmisNow);
			},
			agg: {
				path: 'planerUebertragungquoteAmisNow.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		/// Meine Allianz
		cols.push({
			label: 'mAZ Bestandskunden',
			path: `mazBestandDurchdringungsquote.ze`,
			sortBy: `mazBestandDurchdringungsquote.ze`,
			cellModifier: 'border-left-blue bg-blue',
			labelModifier: 'border-left-blue bg-blue',
			render(row) {
				return simpleFitCell(row.data.mazBestandDurchdringungsquote);
			},
			agg: {
				path: 'mazBestandDurchdringungsquote.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		cols.push(tableDividerColumnWithAgg);

		// PLUS: Social Media
		cols.push({
			label: (
				<>
					Facebook <span className="no-wrap">Fans ✚</span>
				</>
			),
			path: 'fbFanAnzahl.ergebnis',
			sortBy: 'fbFanAnzahl.ergebnis',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.fbFanAnzahl);
			},
			agg: {
				path: 'fbFanAnzahl.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: (
				<>
					Instagram <span className="no-wrap">Follower ✚</span>
				</>
			),
			path: 'instaFollower.ergebnis',
			sortBy: 'instaFollower.ergebnis',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.instaFollower);
			},
			agg: {
				path: 'instaFollower.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		// StS Plus
		cols.push({
			label: (
				<>
					<span className="no-wrap">StS Vertrieb</span> <span className="no-wrap">Schulungsfortschritt ✚</span>
				</>
			),
			path: `stsBasicSchulung`,
			sortBy: `stsBasicSchulung`,
			cellModifier: 'border-left-turquoise bg-turquoise',
			labelModifier: 'border-left-turquoise bg-turquoise',
			render(row) {
				if (!row.data.stsBasicSchulung) return <></>;
				return <>{row.data.stsBasicSchulung}%</>;
			},
			agg: {
				path: 'stsBasicSchulung',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		//  --- PRIME ---
		/// AKUMA
		cols.push({
			label: 'AKUMA Prime Nutzer',
			path: `isAkumaPrimeUser`,
			sortBy: `isAkumaPrimeUser`,
			cellModifier: 'border-left-yellow bg-yellow',
			labelModifier: 'border-left-yellow bg-yellow',
			render(row) {
				return <div className="has-text-centered">{row.data.isAkumaPrimeUser ? <Icon iconClass="check-circle" /> : <></>}</div>;
			},
			agg: {
				path: 'isAkumaPrimeUser',
				aggType: AggregrationType.count,
			},
		});
		cols.push({
			label: (
				<>
					AKUMA Prime <span className="no-wrap">Einordnungsquote ✚</span>
				</>
			),
			path: `primeEinordnungsquote.ze`,
			sortBy: `primeEinordnungsquote.ze`,
			cellModifier: 'bg-yellow',
			labelModifier: 'bg-yellow',
			render(row) {
				return simpleFitCell(row.data.primeEinordnungsquote);
			},
			agg: {
				path: 'primeEinordnungsquote.ze',
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		// VU Inbound KVM
		cols.push({
			label: (
				<>
					<span className="no-wrap">VU Inbound KVM ✚</span>
				</>
			),
			path: `kvmMonthlyAverage`,
			sortBy: `kvmMonthlyAverage`,
			cellModifier: 'border-left-blue bg-blue',
			labelModifier: 'border-left-blue bg-blue',
			render(row) {
				if (!row.data.kvmMonthlyAverage) return <></>;
				return <>{formatPercent(row.data.kvmMonthlyAverage, 1)}</>;
			},
			agg: {
				path: 'kvmMonthlyAverage',
				aggType: AggregrationType.sum,
				format: (v) => formatPercent(v, 1),
			},
		});
		// AMIS.NOW Leads
		cols.push({
			label: (
				<>
					<span className="no-wrap">AMIS.NOW Leads ✚</span>
				</>
			),
			path: `leadNowBrutto`,
			sortBy: `leadNowBrutto`,
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			agg: {
				path: 'leadNowBrutto',
				aggType: AggregrationType.sum,
			},
		});

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'overview', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
				setSelectedAgtId(agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);

			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'overview');
		history.pushWithQuery(p);
	};

	return (
		<>
			{!hasItems ? (
				<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
			) : (
				<>
					{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="overview" onClose={onClose} />}
					<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
				</>
			)}
		</>
	);
});
